import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ============================================================================
// useTableColumnLayout — column UX for plain HTML <table>s in the QS workspace
// ============================================================================
// Brings the AG Grid "layout" behaviour to tables that can't be AG Grids
// (e.g. the Library, which is master-detail + grouped): per-column resize,
// collapse to a sliver, freeze (sticky-left), and drag-to-reorder — all
// remembered in localStorage. There is no row selection (these tables have no
// bulk actions), so unlike the grid hook this one carries no checkbox.
//
// Usage:
//   const cols = [{ id:'code', defaultWidth:130 }, …];  // stable reference
//   …optionally { minWidth } — a floor the saved layout can't go under.
//   const t = useTableColumnLayout({ storageKey, columns: cols });
//   t.orderedIds.forEach((id) => { const c = t.layout[id]; … render … });
// ============================================================================

export const TABLE_COLLAPSED_W = 28;   // sliver width — room for the expand chevron

// Shared width + sticky-freeze style for a header (th) or body (td) cell.
export function colCellStyle(meta, { isHeader = false } = {}) {
  const s = { width: meta.width };
  if (meta.isFrozen) {
    s.position = 'sticky';
    s.left = meta.left;
    s.zIndex = isHeader ? 4 : 2;
    s.background = isHeader ? 'var(--brio-surface-muted, #f5f5f5)' : 'var(--brio-bg, #fff)';
  }
  return s;
}

export function useTableColumnLayout({ storageKey, columns }) {
  const byId = useMemo(() => {
    const m = new Map();
    columns.forEach((c) => m.set(c.id, c));
    return m;
  }, [columns]);
  const defaultOrder = useMemo(() => columns.map((c) => c.id), [columns]);
  const defaultWidths = useMemo(() => {
    const w = {};
    columns.forEach((c) => { w[c.id] = c.defaultWidth; });
    return w;
  }, [columns]);

  const saved = useRef(((() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch { return {}; }
  })())).current;

  const [widths, setWidths] = useState(() => ({ ...defaultWidths, ...(saved.widths || {}) }));
  const [collapsed, setCollapsed] = useState(() => new Set(saved.collapsed || []));
  const [frozen, setFrozen] = useState(() => new Set(saved.frozen || []));
  const [order, setOrder] = useState(() => {
    const savedOrder = Array.isArray(saved.order) ? saved.order.filter((id) => byId.has(id)) : [];
    if (!savedOrder.length) return defaultOrder;
    // A column added since the layout was saved goes in after the column it
    // follows by default (or first, if it leads), so it lands where it was
    // designed to sit rather than at the far end.
    const merged = [...savedOrder];
    defaultOrder.forEach((id, i) => {
      if (merged.includes(id)) return;
      const prev = i > 0 ? defaultOrder[i - 1] : null;
      const at = prev ? merged.indexOf(prev) + 1 : 0;
      merged.splice(at, 0, id);
    });
    return merged;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        widths, collapsed: [...collapsed], frozen: [...frozen], order,
      }));
    } catch { /* storage unavailable — layout just won't persist */ }
  }, [storageKey, widths, collapsed, frozen, order]);

  const toggleCollapse = useCallback((id) => {
    setCollapsed((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const toggleFreeze = useCallback((id) => {
    setFrozen((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);

  const resetLayout = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    setWidths({ ...defaultWidths });
    setCollapsed(new Set());
    setFrozen(new Set());
    setOrder(defaultOrder);
  }, [storageKey, defaultWidths, defaultOrder]);

  // Resize via a pointer drag on the column's right-edge handle.
  const resizing = useRef(null);
  const startResize = useCallback((id, e) => {
    e.preventDefault(); e.stopPropagation();
    const floor = byId.get(id)?.minWidth || TABLE_COLLAPSED_W;
    resizing.current = { id, startX: e.clientX, startW: widths[id] || byId.get(id)?.defaultWidth || 100, floor };
    const onMove = (ev) => {
      const { id: rid, startX, startW, floor: min } = resizing.current || {};
      if (rid == null) return;
      setWidths((prev) => ({ ...prev, [rid]: Math.max(min, startW + (ev.clientX - startX)) }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      resizing.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [widths, byId]);

  // Reorder via native drag-and-drop on the header cell.
  const dragId = useRef(null);
  const onDragStart = useCallback((id) => { dragId.current = id; }, []);
  const onDrop = useCallback((targetId) => {
    const src = dragId.current;
    dragId.current = null;
    if (!src || src === targetId) return;
    setOrder((prev) => {
      const arr = prev.filter((x) => x !== src);
      const idx = arr.indexOf(targetId);
      arr.splice(idx < 0 ? arr.length : idx, 0, src);
      return arr;
    });
  }, []);

  // Per-column resolved layout: width, collapsed/frozen flags, and the
  // sticky-left offset (cumulative width of frozen columns to its left).
  const layout = useMemo(() => {
    let leftAcc = 0;
    const map = {};
    order.forEach((id) => {
      const isC = collapsed.has(id);
      const col = byId.get(id);
      // minWidth is a floor on the *saved* width, not just the default: a
      // column whose content grew (a button added to an actions cell) would
      // otherwise stay clipped forever for anyone who has used the table
      // before, since localStorage wins over defaultWidth. Collapsing still
      // overrides it — that's an explicit choice, not a stale layout.
      const floor = col?.minWidth || 0;
      const w = isC ? TABLE_COLLAPSED_W : Math.max(widths[id] || col?.defaultWidth || 100, floor);
      const isF = frozen.has(id);
      map[id] = { width: w, isCollapsed: isC, isFrozen: isF, left: isF ? leftAcc : null };
      if (isF) leftAcc += w;
    });
    return map;
  }, [order, collapsed, frozen, widths, byId]);

  return {
    orderedIds: order, layout,
    toggleCollapse, toggleFreeze, startResize, onDragStart, onDrop, resetLayout,
  };
}

export default useTableColumnLayout;
